import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Checkbox,
  Button,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Document as PdfDocument, Page } from "react-pdf";
import { Vocabulary } from "../../Utils/Vocabulary";
import { urlEnum } from "../../Utils/urlEnum";
import style from "../../Styles/photoGallery.module.css";

interface ImageCardProps {
  image: any;
  index: number;
  isEditMode: boolean;
  locations: any[];
  selectedLocations: any[];
  isSelectedForDelete: boolean;
  onToggleDelete: (index: number) => void;
  onLocationChange: (imageId: number, locations: any[]) => void;
  onAddToSite: (imageId: number) => void;
  onDeleteFromSite: (imageId: number) => void;
  onPdfClick: (image: any) => void;
}

export default function ImageCard({
  image,
  index,
  isEditMode,
  locations,
  selectedLocations,
  isSelectedForDelete,
  onToggleDelete,
  onLocationChange,
  onAddToSite,
  onDeleteFromSite,
  onPdfClick,
}: ImageCardProps) {
  const isPdf = image.name?.includes(".pdf");
  const isVisibleOnSite = image.status_poza === "1";

  // Sortable hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: image.id,
    disabled: !isEditMode,
  });

  const style2 = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style2}
      {...attributes}
      {...listeners}
      className={`${style.imageCard} ${isDragging ? style.dragging : ""} ${
        isEditMode ? style.draggable : ""
      }`}
    >
      {/* Image Display Section */}
      <div className={style.imageWrapper}>
        {/* Checkbox for delete selection - Only in edit mode */}
        {isEditMode && (
          <div className={style.checkbox}>
            <Checkbox
              size="small"
              onClick={() => onToggleDelete(index)}
              checked={isSelectedForDelete}
              sx={{
                background: "rgba(255, 255, 255, 0.9)",
                borderRadius: "4px",
              }}
            />
          </div>
        )}

        {/* On Site Badge */}
        {isVisibleOnSite && (
          <div className={style.onSiteBadge}>{Vocabulary.visibleOnSite}</div>
        )}

        {/* Image or PDF */}
        {isPdf ? (
          <div className={style.pdfPreview} onClick={() => onPdfClick(image)}>
            <PdfDocument file={`/api/${image.file}`}>
              <Page
                pageNumber={1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={150}
              />
            </PdfDocument>
          </div>
        ) : (
          <img
            src={`/api/${image.file}`}
            alt=""
            className={style.image}
            data-file={`${urlEnum.vehicles}/${image.file}`}
            loading="lazy"
          />
        )}
      </div>

      {/* Location and Action Section */}
      {isEditMode ? (
        <div className={style.locationSection}>
          {/* Location Switches */}
          <div className={style.locationSwitches}>
            {locations?.map((location: any) => {
              const isSelected = selectedLocations.some(
                (loc: any) => loc.id === location.id
              );
              return (
                <FormControlLabel
                  key={location.id}
                  control={
                    <Switch
                      size="small"
                      checked={isSelected}
                      onChange={(e) => {
                        const newLocations = e.target.checked
                          ? [...selectedLocations, location]
                          : selectedLocations.filter(
                              (loc: any) => loc.id !== location.id
                            );
                        onLocationChange(image.id, newLocations);
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#0c7146",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#0c7146",
                          },
                      }}
                    />
                  }
                  label={location.nume}
                  sx={{
                    width: "100%",
                    margin: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "11px",
                      width: "100%",
                    },
                  }}
                />
              );
            })}
          </div>

          {/* Add to Site / Delete from Site Button */}
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() =>
              isVisibleOnSite
                ? onDeleteFromSite(image.id)
                : onAddToSite(image.id)
            }
            sx={{
              backgroundColor: isVisibleOnSite ? "#f44" : "#0c7146",
              "&:hover": {
                backgroundColor: isVisibleOnSite ? "#c33" : "#0a5a38",
              },
              color: "#fff",
              marginTop: "8px",
              padding: "5px",
            }}
          >
            {isVisibleOnSite
              ? Vocabulary.deleteFromSite || "Șterge de pe site"
              : Vocabulary.addOnSite || "Adaugă pe site"}
          </Button>
        </div>
      ) : (
        // Show Chips when NOT in edit mode
        selectedLocations?.length !== 0 && (
          <div className={style.locationChips}>
            {selectedLocations.map((loc: any) => (
              <Chip
                key={loc.id}
                label={loc.nume}
                size="small"
                className={style.locationChip}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}
